/* eslint-disable no-plusplus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import { useDispatch } from 'react-redux';
import { RegisterTypes } from 'store/modules/register_store/types';
import { useHistory } from 'react-router-dom';
import { fetchCompanyByCnpj } from 'store/modules/register_store/actions';
import createRdConversion from 'services/actions/rd_conversion_actions';
import { cleanCnpj, validateCnpj } from 'utils/cnpj';
import { countryCodeSafeMaskProps, isCelularValid, phoneDigits } from 'utils/phone';
import { Container } from './styles';

const RegisterCard: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [fullName, setFullName] = useState<string>('');
  const [cnpj, setCnpj] = useState<string>('');
  const [isValidCnpj, setIsValidCnpj] = useState<boolean>(true);

  const [telephone, setTelephone] = useState<string>('');
  const [telephoneIsValid, setTelephoneIsValid] = useState<boolean>(true);

  const [email, setEmail] = useState<string>('');
  const [emailIsValid, setEmailIsValid] = useState<boolean>(true);

  const [password, setPassword] = useState<string>('');
  const [passwordIsValid, setPasswordIsValid] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  function isCnpjValid(value: string): boolean {
    return validateCnpj(value);
  }

  // 11 dígitos DESCONTADO o código do país: "+5511992812098" tem 13 e não pode ser
  // aceito como se já fosse DDD + número.
  const isTelephoneValid = (value: string) => isCelularValid(value);

  const isEmailValid = (value: string) => {
    return /^\S+@\S+\.\S+$/.test(value.trim());
  };

  const isPasswordValid = (value: string) => {
    return value.trim().length >= 6;
  };

  const handleCnpjBlur = () => {
    const ok = isCnpjValid(cnpj);
    setIsValidCnpj(ok);

    if (ok) {
      dispatch(fetchCompanyByCnpj(cnpj, email));
    }
  };

  const handleTelephoneBlur = () => {
    const ok = isTelephoneValid(telephone);
    setTelephoneIsValid(ok);
  };

  const handleEmailBlur = () => {
    const ok = isEmailValid(email);
    setEmailIsValid(ok);
  };

  const handlePasswordBlur = () => {
    const ok = isPasswordValid(password);
    setPasswordIsValid(ok);
  };

  const isFormValid = useMemo(() => {
    const nameOk = fullName.trim().length > 0;
    const cnpjOk = cnpj.trim().length > 0 && isCnpjValid(cnpj);
    const telOk = telephone.trim().length > 0 && isTelephoneValid(telephone);
    const emailOk = email.trim().length > 0 && isEmailValid(email);
    const passOk = password.trim().length > 0 && isPasswordValid(password);

    return nameOk && cnpjOk && telOk && emailOk && passOk;
  }, [fullName, cnpj, telephone, email, password]);
  const handleStart = () => {
    const cnpjOk = isCnpjValid(cnpj);
    const telOk = isTelephoneValid(telephone);
    const emailOk = isEmailValid(email);
    const passOk = isPasswordValid(password);

    setIsValidCnpj(cnpjOk);
    setTelephoneIsValid(telOk);
    setEmailIsValid(emailOk);
    setPasswordIsValid(passOk);

    const formOk = cnpjOk && telOk && emailOk && passOk;
    if (!formOk) return;

    dispatch({
      type: RegisterTypes.SET_DRAFT,
      data: {
        fullName,
        cnpj,
        telephone,
        email,
        password,
      },
    });

    createRdConversion(
      'cadastro-novo-etapa-1',
      {
        nome: fullName,
        cnpj: cleanCnpj(cnpj),
        telefone: phoneDigits(telephone),
      },
      email,
    );

    history.push('/onboarding');
  };

  return (
    <Container>
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-4">Cadastre-se gratuitamente</h5>

          <form onSubmit={(e) => e.preventDefault()}>
            <div className="form-group mb-3">
              <label>Nome</label>
              <input
                type="text"
                className="form-control"
                placeholder="Seu nome completo"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="form-row mb-3">
              <div className="form-group col-md-6">
                <label>CNPJ</label>
                <InputMask
                  mask="**.***.***/****-99"
                  className={`form-control ${isValidCnpj ? '' : 'is-invalid'}`}
                  id="cnpj"
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onBlur={handleCnpjBlur}
                  onChange={(e) => setCnpj(e.target.value.toUpperCase())}
                />
                {!isValidCnpj && <div className="invalid-feedback">CNPJ inválido.</div>}
              </div>

              <div className="form-group col-md-6">
                <label>WhatsApp</label>
                <InputMask
                  {...countryCodeSafeMaskProps}
                  mask="(99) 99999-9999"
                  className={`form-control ${telephoneIsValid ? '' : 'is-invalid'}`}
                  id="telephone"
                  placeholder="(00) 00000-0000"
                  autoComplete="tel"
                  value={telephone}
                  onBlur={handleTelephoneBlur}
                  onChange={(e) => setTelephone(e.target.value)}
                />
                {!telephoneIsValid && <div className="invalid-feedback">WhatsApp inválido.</div>}
              </div>
            </div>

            <div className="form-group mb-3">
              <label>E-mail</label>
              <input
                type="email"
                className={`form-control ${emailIsValid ? '' : 'is-invalid'}`}
                placeholder="seu@email.com.br"
                autoComplete="email"
                value={email}
                onBlur={handleEmailBlur}
                onChange={(e) => setEmail(e.target.value)}
              />
              {!emailIsValid && <div className="invalid-feedback">E-mail inválido.</div>}
            </div>

            <div className="form-group mb-4">
              <label>Senha</label>
              <div className="password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control ${passwordIsValid ? '' : 'is-invalid'}`}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                  value={password}
                  onBlur={handlePasswordBlur}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <span className="material-icons" aria-hidden="true">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {!passwordIsValid && (
                <div className="invalid-feedback d-block">
                  Senha deve ter no mínimo 6 caracteres.
                </div>
              )}
            </div>

            <button
              type="button"
              className="btn btn-submit btn-block"
              disabled={!isFormValid}
              onClick={handleStart}
            >
              Iniciar teste gratuito
            </button>

            <p className="terms-text text-center mt-3 mb-0">
              Ao confirmar o cadastro, concordo com os{' '}
              <a href="https://conlicitacao.com.br/termos-de-uso" target="_blank" rel="noreferrer">
                termos de uso
              </a>{' '}
              e{' '}
              <a
                href="https://conlicitacao.com.br/politica-de-privacidade/"
                target="_blank"
                rel="noreferrer"
              >
                política de privacidade
              </a>
              .
            </p>
          </form>
        </div>
      </div>
    </Container>
  );
};

export { RegisterCard };
